import { api } from "@/redux/api";

import {
	BulkCreateZohoFieldMappings,
	AiTestRequest,
	AiTestResponse,
	AiTestResponseSchema,
	CreateZohoDealRequest,
	CreateZohoDealResponse,
	CreateZohoDealResponseSchema,
	CreateZohoFieldMapping,
	CreateZohoUserMapping,
	UpdateZohoCompanyDealCreationTrigger,
	UpdateZohoCompanySyncTrigger,
	UpdateZohoConversationSync,
	UpdateZohoFieldMapping,
	UpdateZohoListSyncTrigger,
	ZohoCampaignLink,
	ZohoCampaignLinksResponse,
	ZohoCampaignsResponse,
	ZohoCompanyDealCreationTriggerResponse,
	ZohoCompanySyncTriggerResponse,
	ZohoConversationSyncResponse,
	ZohoDealConfig,
	ZohoDealConfigResponse,
	ZohoEntityType,
	ZohoFieldMapping,
	ZohoFieldMappingPreviewRequest,
	ZohoFieldMappingPreviewResponse,
	ZohoFieldMappingPreviewResponseSchema,
	ZohoFieldMappingsResponse,
	ZohoFieldsResponse,
	ZohoListSyncTriggerResponse,
	ZohoOAuthConnectResponse,
	ZohoOAuthDisconnectResponse,
	ZohoOAuthStatusResponse,
	ZohoOwnersResponse,
	ZohoPipelinesResponse,
	ZohoResyncResponse,
	ZohoSourceFieldsResponse,
	ZohoSyncLogsResponse,
	ZohoTestSyncRequest,
	ZohoTestSyncResponse,
	ZohoTestSyncResponseSchema,
	ZohoUserMapping,
	ZohoUserMappingsResponse,
} from "../../pages/Company/schema/zoho";

export const zohoApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// Field Retrieval
		getZohoLeadFields: builder.query<ZohoFieldsResponse, void>({
			query: () => ({
				url: "zoho/fields/leads",
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),
		getZohoContactFields: builder.query<ZohoFieldsResponse, void>({
			query: () => ({
				url: "zoho/fields/contacts",
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),
		getZohoAccountFields: builder.query<ZohoFieldsResponse, void>({
			query: () => ({
				url: "zoho/fields/accounts",
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),
		getZohoDealFields: builder.query<ZohoFieldsResponse, void>({
			query: () => ({
				url: "zoho/fields/deals",
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),
		getZohoSourceFields: builder.query<ZohoSourceFieldsResponse, void>({
			query: () => ({
				url: "zoho/source-fields",
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),

		// Field Mapping CRUD
		getZohoFieldMappings: builder.query<ZohoFieldMappingsResponse, { entityType?: ZohoEntityType }>(
			{
				query: (params) => ({
					url: "zoho/field-mappings",
					method: "GET",
					params,
				}),
				providesTags: ["Zoho"],
			}
		),
		createZohoFieldMapping: builder.mutation<ZohoFieldMapping, CreateZohoFieldMapping>({
			query: (body) => ({
				url: "zoho/field-mappings",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Zoho"],
		}),
		updateZohoFieldMapping: builder.mutation<
			ZohoFieldMapping,
			{ id: number; data: UpdateZohoFieldMapping }
		>({
			query: ({ id, data }) => ({
				url: `zoho/field-mappings/${id}`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: ["Zoho"],
		}),
		deleteZohoFieldMapping: builder.mutation<void, number>({
			query: (id) => ({
				url: `zoho/field-mappings/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["Zoho"],
		}),
		bulkCreateZohoFieldMappings: builder.mutation<
			ZohoFieldMappingsResponse,
			BulkCreateZohoFieldMappings
		>({
			query: (body) => ({
				url: "zoho/field-mappings/bulk",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Zoho"],
		}),
		testZohoAiFieldMapping: builder.mutation<AiTestResponse, AiTestRequest>({
			query: (body) => ({
				url: "zoho/field-mappings/ai/test",
				method: "POST",
				body,
				responseSchema: AiTestResponseSchema,
			}),
		}),
		previewZohoFieldMappings: builder.mutation<
			ZohoFieldMappingPreviewResponse,
			ZohoFieldMappingPreviewRequest
		>({
			query: (body) => ({
				url: "zoho/field-mappings/preview",
				method: "POST",
				body,
				responseSchema: ZohoFieldMappingPreviewResponseSchema,
			}),
		}),
		testZohoSync: builder.mutation<ZohoTestSyncResponse, ZohoTestSyncRequest>({
			query: (body) => ({
				url: "zoho/sync/test",
				method: "POST",
				body,
				responseSchema: ZohoTestSyncResponseSchema,
			}),
			invalidatesTags: ["Zoho"],
		}),

		// Sync Trigger Settings - Company
		getZohoCompanySyncTrigger: builder.query<ZohoCompanySyncTriggerResponse, void>({
			query: () => ({
				url: "zoho/sync-trigger/company",
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),
		updateZohoCompanySyncTrigger: builder.mutation<
			ZohoCompanySyncTriggerResponse,
			UpdateZohoCompanySyncTrigger
		>({
			query: (body) => ({
				url: "zoho/sync-trigger/company",
				method: "PUT",
				body,
			}),
			invalidatesTags: ["Zoho", "Me", "Company"],
		}),

		// Sync Trigger Settings - List
		getZohoListSyncTrigger: builder.query<ZohoListSyncTriggerResponse, number>({
			query: (listId) => ({
				url: `zoho/sync-trigger/list/${listId}`,
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),
		updateZohoListSyncTrigger: builder.mutation<
			ZohoListSyncTriggerResponse,
			{ listId: number; data: UpdateZohoListSyncTrigger }
		>({
			query: ({ listId, data }) => ({
				url: `zoho/sync-trigger/list/${listId}`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: ["Zoho"],
		}),

		// Deal Creation Trigger Settings - Company
		getZohoCompanyDealCreationTrigger: builder.query<ZohoCompanyDealCreationTriggerResponse, void>({
			query: () => ({
				url: "zoho/deal-creation-trigger",
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),
		updateZohoCompanyDealCreationTrigger: builder.mutation<
			ZohoCompanyDealCreationTriggerResponse,
			UpdateZohoCompanyDealCreationTrigger
		>({
			query: (body) => ({
				url: "zoho/deal-creation-trigger",
				method: "PUT",
				body,
			}),
			invalidatesTags: ["Zoho", "Me", "Company"],
		}),

		// Pipelines
		getZohoPipelines: builder.query<ZohoPipelinesResponse, void>({
			query: () => ({
				url: "zoho/pipelines",
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),

		// Owners
		getZohoOwners: builder.query<ZohoOwnersResponse, void>({
			query: () => ({
				url: "zoho/owners",
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),

		// Deal Configuration
		getZohoDealConfig: builder.query<ZohoDealConfigResponse, void>({
			query: () => ({
				url: "zoho/deal-config",
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),
		saveZohoDealConfig: builder.mutation<ZohoDealConfigResponse, ZohoDealConfig>({
			query: (body) => ({
				url: "zoho/deal-config",
				method: "PUT",
				body,
			}),
			invalidatesTags: ["Zoho"],
		}),

		// User Mappings
		getZohoUserMappings: builder.query<ZohoUserMappingsResponse, void>({
			query: () => ({
				url: "zoho/user-mappings",
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),
		createZohoUserMapping: builder.mutation<ZohoUserMapping, CreateZohoUserMapping>({
			query: (body) => ({
				url: "zoho/user-mappings",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Zoho"],
		}),
		deleteZohoUserMapping: builder.mutation<void, number>({
			query: (id) => ({
				url: `zoho/user-mappings/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["Zoho"],
		}),

		// Create Deal
		createZohoDeal: builder.mutation<CreateZohoDealResponse, CreateZohoDealRequest>({
			query: (body) => ({
				url: "zoho/deals/create",
				method: "POST",
				body,
				responseSchema: CreateZohoDealResponseSchema,
			}),
			invalidatesTags: ["Zoho"],
		}),

		// Conversation Sync
		getZohoConversationSync: builder.query<ZohoConversationSyncResponse, void>({
			query: () => ({
				url: "zoho/conversation-sync",
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),
		updateZohoConversationSync: builder.mutation<
			ZohoConversationSyncResponse,
			UpdateZohoConversationSync
		>({
			query: (body) => ({
				url: "zoho/conversation-sync",
				method: "PUT",
				body,
			}),
			invalidatesTags: ["Zoho"],
		}),

		// OAuth
		getZohoOAuthConnectUrl: builder.query<ZohoOAuthConnectResponse, void>({
			query: () => ({
				url: "zoho/oauth/connect",
				method: "GET",
			}),
		}),
		getZohoOAuthStatus: builder.query<ZohoOAuthStatusResponse, void>({
			query: () => ({
				url: "zoho/oauth/status",
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),
		disconnectZohoOAuth: builder.mutation<ZohoOAuthDisconnectResponse, void>({
			query: () => ({
				url: "zoho/oauth",
				method: "DELETE",
			}),
			invalidatesTags: ["Zoho", "Me", "Company"],
		}),

		// Manual Resync
		triggerZohoResync: builder.mutation<ZohoResyncResponse, void>({
			query: () => ({
				url: "zoho/sync/resync",
				method: "POST",
			}),
			invalidatesTags: ["Zoho"],
		}),

		// Sync Logs
		getZohoSyncLogs: builder.query<
			ZohoSyncLogsResponse,
			{ entityType?: string; limit?: number; offset?: number } | void
		>({
			query: (params) => ({
				url: "zoho/sync/logs",
				method: "GET",
				params: params || undefined,
			}),
			providesTags: ["Zoho"],
		}),

		// Campaign Links
		getZohoCampaignLinks: builder.query<ZohoCampaignLinksResponse, void>({
			query: () => ({
				url: "zoho/campaign-links",
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),
		getZohoCampaignLinkForList: builder.query<ZohoCampaignLink | null, number>({
			query: (listId) => ({
				url: `zoho/campaign-links/list/${listId}`,
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),
		getZohoCampaigns: builder.query<ZohoCampaignsResponse, void>({
			query: () => ({
				url: "zoho/campaigns",
				method: "GET",
			}),
			providesTags: ["Zoho"],
		}),
		linkZohoListToCampaign: builder.mutation<
			ZohoCampaignLink,
			{ listId: number; zohoCampaignId: string; zohoCampaignName?: string }
		>({
			query: ({ listId, ...body }) => ({
				url: `zoho/campaign-links/list/${listId}`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["Zoho"],
		}),
		unlinkZohoListFromCampaign: builder.mutation<{ success: boolean }, number>({
			query: (listId) => ({
				url: `zoho/campaign-links/list/${listId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["Zoho"],
		}),
	}),
});

export const {
	useGetZohoLeadFieldsQuery,
	useGetZohoContactFieldsQuery,
	useGetZohoAccountFieldsQuery,
	useGetZohoDealFieldsQuery,
	useGetZohoSourceFieldsQuery,
	useGetZohoFieldMappingsQuery,
	useCreateZohoFieldMappingMutation,
	useUpdateZohoFieldMappingMutation,
	useDeleteZohoFieldMappingMutation,
	useBulkCreateZohoFieldMappingsMutation,
	useTestZohoAiFieldMappingMutation,
	usePreviewZohoFieldMappingsMutation,
	useTestZohoSyncMutation,
	useGetZohoCompanySyncTriggerQuery,
	useUpdateZohoCompanySyncTriggerMutation,
	useGetZohoListSyncTriggerQuery,
	useUpdateZohoListSyncTriggerMutation,
	useGetZohoCompanyDealCreationTriggerQuery,
	useUpdateZohoCompanyDealCreationTriggerMutation,
	useGetZohoPipelinesQuery,
	useGetZohoOwnersQuery,
	useGetZohoDealConfigQuery,
	useSaveZohoDealConfigMutation,
	useGetZohoUserMappingsQuery,
	useCreateZohoUserMappingMutation,
	useDeleteZohoUserMappingMutation,
	useCreateZohoDealMutation,
	useGetZohoConversationSyncQuery,
	useUpdateZohoConversationSyncMutation,
	useLazyGetZohoOAuthConnectUrlQuery,
	useGetZohoOAuthStatusQuery,
	useDisconnectZohoOAuthMutation,
	useTriggerZohoResyncMutation,
	useGetZohoSyncLogsQuery,
	useGetZohoCampaignLinksQuery,
	useGetZohoCampaignLinkForListQuery,
	useGetZohoCampaignsQuery,
	useLinkZohoListToCampaignMutation,
	useUnlinkZohoListFromCampaignMutation,
} = zohoApi;

export const zohoApiReducer = zohoApi.reducer;
export const zohoApiMiddleware = zohoApi.middleware;
